//@flow
import * as React from 'react';
import ReactDOM from 'react-dom';
import withHandlers from 'recompose/withHandlers';
import withStateHandlers from 'recompose/withStateHandlers';
import compose from 'recompose/compose';
import lifecycle from 'recompose/lifecycle';
import { themr } from 'react-css-themr';
import { Table, Column, Cell } from 'fixed-data-table-2';
import { AutoSizer } from 'react-virtualized';
import type {
    EntityImportHeaderModelType,
    EntityImportRowModelType
} from 'common/models/flow/EntityImportTable';
import theme from './FixedDataTable.scss';
import classnames from 'classnames';
import type { List, Map } from 'immutable';

type CellRenderProps = {
    columnKey: string,
    rowIndex: number
};

export type FixedDataTableGetCellPropsTypes = {
    columnKey: string,
    rowIndex: number,
    rowId: string,
    row: EntityImportRowModelType,
    header: EntityImportHeaderModelType
};

type DefaultCellProps = {
    content: string | any,
    theme: Object,
    cellWrapperClassName?: string
};

const DefaultCell = ({
    content,
    theme,
    cellWrapperClassName
}: DefaultCellProps): React.Node => {
    return (
        <Cell className={classnames([theme.cell, cellWrapperClassName])}>
            <div className={theme.text}>{content}</div>
        </Cell>
    );
};
const initializeColumnWidths = {
    UNSAFE_componentWillMount() {
        const { initializeColumnModel } = this.props;
        initializeColumnModel(this.props);
    },
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { initializeColumnModel } = this.props;

        initializeColumnModel(nextProps);
    }
};

const TableResizeHandlers = compose(
    withStateHandlers(
        {
            columnWidths: null
        },
        {
            setColumnWidths: () => columnWidths => {
                return { columnWidths };
            }
        }
    ),
    withHandlers({
        onColumnResizeEnd: ({ setColumnWidths, columnWidths }) => (
            newColumnWidth: number,
            columnKey: string | number
        ): void => {
            const newColumnWidths = {
                ...columnWidths,
                [columnKey]: newColumnWidth
            };
            setColumnWidths(newColumnWidths);
        },
        initializeColumnModel: ({ columnWidths, setColumnWidths }) => props => {
            if (props.tableData && !columnWidths) {
                const { headers } = props.tableData;
                let columnModel = {};
                headers.forEach(header => {
                    columnModel = {
                        ...(columnModel || {}),
                        [header.id]: 150
                    };
                });
                setColumnWidths(columnModel);
            }
        }
    }),
    lifecycle(initializeColumnWidths)
);

type Props = {
    tableData: {
        rows: List<Map<*, *> | any>,
        headers: List<Map<*, string> | any>
    },
    renderCell?: ({
        columnKey: string,
        rowIndex: number,
        rowId: string,
        content: string
    }) => React.ComponentType<any>,
    getCellProps?: FixedDataTableGetCellPropsTypes => Object,
    columnWidths: Array<{ [key: string]: number }>,
    onColumnResizeEnd: (
        newColumnWidth: number,
        columnKey: string | number
    ) => void,
    renderExtraColumns: () => React.ComponentType<any>,
    headerHeight: number,
    rowHeight: number,
    theme: Object,
    disableAutoSizer: boolean,
    disableScrollbars: boolean,
    renderColumn?: ({ header: EntityImportHeaderModelType }) => ?React.Node
};
const fixedDataTableFactory = ({
    TableComponent = Table,
    ColumnComponent = Column,
    CellComponent = DefaultCell,
    HeaderCellComponent = Cell
}: {
    TableComponent: React.ComponentType<any>,
    ColumnComponent: React.ComponentType<any>,
    CellComponent: React.ComponentType<any>,
    HeaderCellComponent: React.ComponentType<any>
}) => {
    class FixedDataTableComponent extends React.PureComponent<Props> {
        static defaultProps = {
            headerHeight: 22,
            rowHeight: 36,
            disableAutoSizer: false,
            disableScrollbars: false
        };

        tableRef = React.createRef();
        tableDOMNode = null;

        renderCells = ({
            columnKey,
            rowIndex,
            header,
            colIndex
        }: CellRenderProps) => {
            const {
                tableData: { rows },
                renderCell,
                getCellProps,
                theme
            } = this.props;
            const row = rows.get(rowIndex);
            const rowId = row.get('id');
            const otherProps =
                getCellProps?.({
                    columnKey,
                    rowIndex,
                    rowId,
                    row,
                    header,
                    colIndex
                }) || {};

            const cellData = row.get(columnKey);
            const cell = renderCell?.({
                columnKey,
                rowIndex,
                rowId,
                content: cellData,
                ...otherProps // might need to override some props, hence spreading otherProps in the end
            });
            if (cell) {
                return cell; // use renderCell to override rendering of individual cells
            }
            const key = `${columnKey}.${rowIndex}`;
            const cellFocus = otherProps?.onFocus;
            otherProps.onFocus = (...args) => this.onFocus(args, cellFocus);
            return (
                <CellComponent
                    key={key}
                    rowId={rowId}
                    headerId={columnKey}
                    content={cellData}
                    row={row}
                    header={header}
                    theme={theme}
                    {...otherProps} // might need to override some props, hence spreading otherProps in the end
                />
            );
        };

        onFocus = (args, callback?: Function): void => {
            if (callback) {
                callback(...args);
            }
            this.resetTableDefaultScroll();
        };

        renderColumns = (): ?List<*> => {
            const { columnWidths, theme, renderColumn } = this.props;
            if (columnWidths) {
                const { headers } = this.props.tableData;
                return headers.map((header, colIndex) => {
                    const column = renderColumn?.({
                        header,
                        colIndex,
                        columnWidths,
                        resetTableDefaultScroll: this.resetTableDefaultScroll
                    });
                    if (column || column === null) {
                        // return null when don't want column to be rendered
                        return column;
                    }
                    const headerCell = (
                        <HeaderCellComponent className={theme.tableHeader}>
                            <span className={theme.text}>{header.title}</span>
                        </HeaderCellComponent>
                    );

                    return (
                        <ColumnComponent
                            columnKey={header.id}
                            key={header.id}
                            isResizable
                            isReorderable={false}
                            allowCellsRecycling
                            header={headerCell}
                            // eslint-disable-next-line react/jsx-no-bind
                            cell={({ columnKey, rowIndex }) =>
                                this.renderCells({
                                    columnKey,
                                    rowIndex,
                                    header,
                                    colIndex
                                })
                            }
                            width={columnWidths[header.id]}
                            fixed={header.fixed}
                        />
                    );
                });
            }

            return null;
        };

        resetTableDefaultScroll = () => {
            // FIX - For Browser default api shifts main div of table when focusing on partially visible cell
            if (this.tableRef.current) {
                if (!this.tableDOMNode) {
                    this.tableDOMNode = ReactDOM.findDOMNode(
                        this.tableRef.current
                    );
                }
                // move back div position to default
                if (this.tableDOMNode.scrollLeft > 0) {
                    this.tableDOMNode.scrollLeft = 0;
                }
                if (this.tableDOMNode.scrollTop > 0) {
                    this.tableDOMNode.scrollTop = 0;
                }
            }
        };

        render() {
            const {
                tableData,
                onColumnResizeEnd,
                renderExtraColumns,
                headerHeight,
                rowHeight,
                disableAutoSizer,
                disableScrollbars,
                scrollToColumn,
                scrollToRow,
                onScrollEnd,
                rowsSize
            } = this.props;
            const { rows } = tableData;
            const rowCount = rowsSize || rows?.size || 0;
            /* This is required when we want to set the height based on table content.
            Autosizer sets the height to fill the parent, but not stretch it */
            const computedHeight = rowCount * rowHeight + headerHeight;
            return (
                <div className={theme.tableAutosizerContainer}>
                    <AutoSizer disableHeight={disableAutoSizer}>
                        {({ height, width }) => {
                            return (
                                <TableComponent
                                    stopReactWheelPropagation
                                    scrollToColumn={scrollToColumn}
                                    scrollToRow={scrollToRow}
                                    onScrollEnd={onScrollEnd}
                                    theme={theme}
                                    rowHeight={rowHeight}
                                    rowsCount={rowCount}
                                    height={
                                        (disableAutoSizer && computedHeight) ||
                                        height
                                    }
                                    width={width}
                                    overflowX={
                                        disableScrollbars ? 'hidden' : 'auto'
                                    }
                                    overflowY={
                                        disableScrollbars ? 'hidden' : 'auto'
                                    }
                                    isColumnResizing={false}
                                    onColumnResizeEndCallback={
                                        onColumnResizeEnd
                                    }
                                    headerHeight={headerHeight}
                                    ref={this.tableRef}
                                >
                                    {renderExtraColumns && renderExtraColumns()}
                                    {this.renderColumns()}
                                </TableComponent>
                            );
                        }}
                    </AutoSizer>
                </div>
            );
        }
    }

    const ThemedFixedDataTable = TableResizeHandlers(
        themr('FixedDataTableComponent', theme)(FixedDataTableComponent)
    );
    return ThemedFixedDataTable;
};
const FixedDataTable = fixedDataTableFactory({});
export { FixedDataTable, TableResizeHandlers, fixedDataTableFactory };
export default FixedDataTable;
